import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, LockOpen, Globe, Users, Loader2, Shield, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { getApiBase } from '../../utils/apiBase';
import { PRIVACY_MODES, privacyModeLabel } from '../../utils/archivePrivacy';
import { toast } from 'sonner';

const API = getApiBase();

const MODE_ICONS = {
  private: Lock,
  invite_only: Users,
  published: Globe,
};

export function ArchivePrivacyLock({ treeId, isOwner, className }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchPrivacy = useCallback(async () => {
    if (!treeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trees/${treeId}/privacy`);
      setData(res.data);
    } catch {
      toast.error('Could not load privacy settings');
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  useEffect(() => {
    fetchPrivacy();
  }, [fetchPrivacy]);

  const setMode = async (mode) => {
    if (!isOwner || saving) return;
    if (mode === 'published') {
      const ok = window.confirm(
        'Publish a read-only memory view?\n\nAnyone with the link can read polished memories — not edit, and never your private vault. You can switch back to private anytime.'
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/trees/${treeId}/privacy`, { privacy_mode: mode });
      setData(res.data);
      toast.success(`Archive is now ${privacyModeLabel(mode)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update privacy');
    } finally {
      setSaving(false);
    }
  };

  const copyPublishedLink = () => {
    if (!data?.published_url) return;
    const url = `${window.location.origin}${data.published_url}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Published link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className={cn('flex justify-center py-8', className)} data-testid="privacy-lock-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#8b7355]" aria-label="Loading privacy settings" />
      </div>
    );
  }

  const current = data?.privacy_mode || 'private';
  const CurrentIcon = data?.is_locked !== false ? Lock : LockOpen;

  return (
    <section
      className={cn(
        'rounded-2xl border border-[hsl(220_14%_86%)] bg-gradient-to-br from-[hsl(220_22%_99%)] to-[hsl(42_28%_99%)] p-5 sm:p-6',
        className
      )}
      data-testid="archive-privacy-lock"
      aria-label="Archive privacy"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(220_16%_88%)] bg-white/80 text-[hsl(220_20%_35%)]">
          <CurrentIcon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-[hsl(32_10%_42%)]">
            Privacy lock
          </p>
          <h3 className="mt-0.5 font-serif text-lg text-[hsl(24_14%_16%)]">
            {data?.privacy_mode_label || privacyModeLabel(current)}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            New archives start private. You choose if and when to invite family or publish a memory view.
          </p>
        </div>
      </div>

      {isOwner ? (
        <div className="mt-5 grid gap-2 sm:grid-cols-3">
          {PRIVACY_MODES.map((mode) => {
            const Icon = MODE_ICONS[mode.id] || Lock;
            const active = current === mode.id;
            return (
              <button
                key={mode.id}
                type="button"
                disabled={saving}
                onClick={() => setMode(mode.id)}
                className={cn(
                  'rounded-xl border px-3 py-3 text-left transition-colors',
                  active
                    ? 'border-[hsl(43_48%_58%_/_0.5)] bg-[hsl(42_28%_99%)] ring-1 ring-[hsl(43_48%_58%_/_0.35)]'
                    : 'border-[hsl(38_16%_88%)] bg-white/60 hover:bg-white'
                )}
                data-testid={`privacy-mode-${mode.id}`}
              >
                <Icon className="mb-2 h-4 w-4 text-[#8b7355]" aria-hidden />
                <p className="font-serif text-sm text-[hsl(24_14%_16%)]">{mode.label}</p>
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{mode.description}</p>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="mt-4 rounded-lg border border-dashed border-[hsl(38_16%_86%)] px-3 py-2 text-xs text-muted-foreground">
          Only the archive owner can change privacy settings. Your access: {data?.effective_permission || 'view'}.
        </p>
      )}

      {current === 'published' && data?.published_url ? (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-[hsl(38_16%_88%)] bg-white/50 px-3 py-2.5">
          <Globe className="h-4 w-4 text-[#8b7355]" aria-hidden />
          <code className="flex-1 truncate text-xs text-[hsl(32_10%_38%)]">{data.published_url}</code>
          <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={copyPublishedLink}>
            {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
            Copy link
          </Button>
        </div>
      ) : null}

      <div className="mt-4 flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground">
        <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Archives never auto-merge with other families. Possible connection overlaps are suggestions only — you review and approve before anything connects.
        </span>
      </div>
    </section>
  );
}

export default ArchivePrivacyLock;
